import React from "react";

/**
 * Archi's mark: a heavy, open "A" — two strokes meeting at a sharp apex —
 * with a single node where the crossbar would be. One colour (`currentColor`),
 * so it is black on light, white on dark, and white on the dark launcher.
 *
 * While Archi works the same A comes alive in three movements, one after the
 * other, so it never loops on one gesture:
 *   1. it spins once on its vertical axis, in depth, tracing an orbit beneath it;
 *   2. it nods, tipping back and forth, drawing a wave beneath it;
 *   3. it turns back with a small lift, ruling a straight line beneath it.
 * The movements and the lines share one 6s cycle (`archi-a3d`, `archi-under`
 * in tailwind.config.js), each line drawn during its movement.
 */

/** The cycle, and one movement of it. Shared with the keyframes. */
const CYCLE_S = 6;
const MOVEMENTS = 3;

const A: React.FC<{
  size: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ size, className, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className={className}
    style={style}
  >
    <path
      d="M3.5 21 12 3l8.5 18"
      stroke="currentColor"
      strokeWidth={3.2}
      strokeLinecap="square"
      strokeLinejoin="miter"
    />
    <circle cx={12} cy={15} r={2.3} fill="currentColor" />
  </svg>
);

/** What the A draws beneath itself, one line per movement. */
const UNDER_LINES = [
  // An orbit seen in perspective.
  "M3 5a9 2.6 0 1 0 18 0a9 2.6 0 1 0 -18 0",
  // A wave.
  "M2 5q2.5 -3.5 5 0t5 0t5 0t5 0",
  // A ruled line.
  "M3 5h18",
];

const ArchiAtWork: React.FC<{ size: number; className: string }> = ({
  size,
  className,
}) => (
  <span
    className={`inline-flex shrink-0 flex-col items-center ${className}`}
    // Depth for the spin; close enough that the turn reads as 3D at this size.
    style={{ perspective: size * 3 }}
    aria-hidden
  >
    <A
      size={size}
      className="motion-safe:animate-archi-a3d"
      style={{ transformStyle: "preserve-3d" }}
    />
    <svg
      width={size * 1.1}
      height={size * 0.45}
      viewBox="0 0 24 10"
      fill="none"
      className="-mt-0.5"
    >
      {UNDER_LINES.map((d, movement) => (
        <path
          key={d}
          d={d}
          stroke="currentColor"
          strokeWidth={0.9}
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray="100"
          className="motion-safe:animate-archi-under"
          // One keyframe set for the three lines, each a movement apart.
          style={{
            animationDelay: `${-((MOVEMENTS - movement) % MOVEMENTS) * (CYCLE_S / MOVEMENTS)}s`,
          }}
        />
      ))}
    </svg>
  </span>
);

const ArchiMark: React.FC<{
  size?: number;
  working?: boolean;
  className?: string;
}> = ({ size = 20, working = false, className = "" }) =>
  working ? (
    <ArchiAtWork size={size} className={className} />
  ) : (
    <A size={size} className={className} />
  );

export default ArchiMark;
